import { Text } from 'components';
import { TagSelector } from 'components/TagSelector';
import { sendAllMessageStrings } from 'constants/strings';
import { useCurrentUser, useTags } from 'hooks';
import {
  EOrganizationFeature,
  ESendRecipient,
  TMessageSendOption,
} from 'types';
import { cn } from 'utils/cn';
import {
  canFetchOrganizationFeature,
  isOrganizationFeatureEnabled,
} from 'utils/organizationFeature';

type Props = {
  sendOption: TMessageSendOption;
  onSendOptionChange: (option: Partial<TMessageSendOption>) => void;
  className?: string;
};

const recipientStrings = sendAllMessageStrings.recipient;

function RecipientSelector({
  sendOption,
  onSendOptionChange,
  className,
}: Props) {
  const { data: currentUser } = useCurrentUser();
  const canUseTags = isOrganizationFeatureEnabled(
    currentUser,
    EOrganizationFeature.TAG_MANAGEMENT,
  );
  // タグ管理がOFFの組織ではタグAPIが403になるので取得しない
  const {
    tags,
    loading: tagsLoading,
    error: tagsError,
  } = useTags({
    enabled: canFetchOrganizationFeature(currentUser, EOrganizationFeature.TAG_MANAGEMENT),
  });
  const selectedTagIds = sendOption.tagIds ?? [];
  const isFilter = sendOption.recipient === ESendRecipient.FILTER;
  const isUntagged = sendOption.recipient === ESendRecipient.UNTAGGED;

  const toggleTag = (tagId: number) => {
    const next = selectedTagIds.includes(tagId)
      ? selectedTagIds.filter((id) => id !== tagId)
      : [...selectedTagIds, tagId];
    onSendOptionChange({ tagIds: next });
  };

  return (
    <div className={cn('flex flex-wrap gap-4 pt-4', className)}>
      <Text className="py-2" bold>
        {recipientStrings.label}
      </Text>

      <div className="flex flex-col gap-3 pl-0 py-2 sm:pl-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="recipient"
            value={ESendRecipient.ALL}
            checked={sendOption.recipient === ESendRecipient.ALL}
            onChange={() =>
              onSendOptionChange({
                recipient: ESendRecipient.ALL,
                tagIds: undefined,
              })
            }
            className="w-4 h-4 text-primary focus:ring-primary"
          />
          <Text>{recipientStrings.all}</Text>
        </label>

        {!canUseTags && (isFilter || isUntagged) && (
          <p className="pl-6 text-[11px] font-medium text-danger">
            {recipientStrings.tagRecipientUnavailable.replace(
              '${recipient}',
              isFilter ? recipientStrings.filter : recipientStrings.untagged,
            )}
          </p>
        )}

        {canUseTags && (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="recipient"
              value={ESendRecipient.FILTER}
              checked={isFilter}
              onChange={() =>
                onSendOptionChange({ recipient: ESendRecipient.FILTER })
              }
              className="w-4 h-4 text-primary focus:ring-primary"
            />
            <Text>{recipientStrings.filter}</Text>
          </label>
        )}

        {canUseTags && (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="recipient"
              value={ESendRecipient.UNTAGGED}
              checked={isUntagged}
              onChange={() =>
                onSendOptionChange({
                  recipient: ESendRecipient.UNTAGGED,
                  tagIds: undefined,
                })
              }
              className="w-4 h-4 text-primary focus:ring-primary"
            />
            <Text>{recipientStrings.untagged}</Text>
          </label>
        )}

        {canUseTags && isUntagged && (
          <p className="pl-6 text-[11px] font-medium text-muted">
            {recipientStrings.untaggedHint}
          </p>
        )}

        {canUseTags &&
          isFilter &&
          (tagsLoading ? (
            <p className="pl-6 text-[11px] font-medium text-muted">
              {recipientStrings.tagLoading}
            </p>
          ) : tagsError ? (
            <p className="pl-6 text-[11px] font-medium text-danger">
              {tagsError}
            </p>
          ) : tags.length === 0 ? (
            <p className="pl-6 text-[11px] font-medium text-muted">
              {recipientStrings.tagEmpty}
            </p>
          ) : (
            <div className="flex flex-col gap-2 pl-6">
              <TagSelector
                tags={tags}
                selectedTagIds={selectedTagIds}
                onToggle={toggleTag}
              />
              <p className="text-[11px] font-medium text-muted">
                {recipientStrings.tagHint}
              </p>
            </div>
          ))}
      </div>
    </div>
  );
}

export default RecipientSelector;
